import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Save, Eye, EyeOff, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, LocateFixed } from "lucide-react";
import DemoOverlay from "@/components/gameplay/DemoOverlay";

/**
 * Admin backoffice — configurable DEMO ("Aprender a Jogar") guide phrases.
 *
 * Each phrase has:
 *   • Text in 3 languages (PT/EN/ES)
 *   • Colour, font size, effect, duration, enabled
 *   • Free-form screen position via (offset_x, offset_y) in pixels —
 *     origin is the viewport center. Four arrow buttons let admins move
 *     the phrase pixel-by-pixel in the live preview.
 */
const EFFECT_OPTIONS = [
  { v: "fade", l: "Fade" },
  { v: "slide", l: "Slide" },
  { v: "pulse", l: "Pulse" },
  { v: "none", l: "Nenhum" },
];
const LANG_OPTIONS = [
  { v: "pt", l: "PT" },
  { v: "en", l: "EN" },
  { v: "es", l: "ES" },
];

const PHRASE_LABELS = {
  phrase_1: "1 · Verificar a letra na roleta",
  phrase_2: "2 · Identificar a pergunta",
  phrase_3: "3 · Escolher resposta",
  phrase_4_win: "4a · Ganhou super-letra",
  phrase_4_star: "4b · Ganhou super-estrela",
  phrase_4_repeat: "4c · Super-letra repetida",
  phrase_5_final: "5 · Vitória — completou SUPERT",
};

const STEP = 10;        // pixels per click
const BIG_STEP = 50;    // pixels per Shift+click
const CLAMP = 2000;     // backend-enforced bound

function ArrowPad({ offsetX, offsetY, onChange }) {
  const nudge = (dx, dy, evt) => {
    const factor = evt?.shiftKey ? BIG_STEP : STEP;
    onChange({
      offset_x: Math.max(-CLAMP, Math.min(CLAMP, offsetX + dx * factor)),
      offset_y: Math.max(-CLAMP, Math.min(CLAMP, offsetY + dy * factor)),
    });
  };
  const resetCenter = () => onChange({ offset_x: 0, offset_y: 0 });
  const btn =
    "w-9 h-9 grid place-items-center rounded-lg bg-sky-100 hover:bg-sky-200 text-sky-900 shadow-sm transition active:scale-95";
  return (
    <div className="flex items-center gap-4">
      <div className="grid grid-cols-3 grid-rows-3 gap-1 w-fit">
        <div />
        <button
          type="button"
          title="Subir (Shift para 50px)"
          onClick={(e) => nudge(0, -1, e)}
          className={btn}
          data-testid="arrow-up"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <div />
        <button
          type="button"
          title="Esquerda (Shift para 50px)"
          onClick={(e) => nudge(-1, 0, e)}
          className={btn}
          data-testid="arrow-left"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Centrar (0,0)"
          onClick={resetCenter}
          className="w-9 h-9 grid place-items-center rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 shadow-sm transition active:scale-95"
          data-testid="arrow-center"
        >
          <LocateFixed className="w-4 h-4" />
        </button>
        <button
          type="button"
          title="Direita (Shift para 50px)"
          onClick={(e) => nudge(1, 0, e)}
          className={btn}
          data-testid="arrow-right"
        >
          <ArrowRight className="w-4 h-4" />
        </button>
        <div />
        <button
          type="button"
          title="Descer (Shift para 50px)"
          onClick={(e) => nudge(0, 1, e)}
          className={btn}
          data-testid="arrow-down"
        >
          <ArrowDown className="w-4 h-4" />
        </button>
        <div />
      </div>
      <div className="flex-1 grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-400">Offset X (px)</span>
          <input
            type="number"
            min={-CLAMP}
            max={CLAMP}
            step={1}
            value={offsetX}
            onChange={(e) => onChange({ offset_x: Math.max(-CLAMP, Math.min(CLAMP, Number(e.target.value) || 0)) })}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"
            data-testid="input-offset-x"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-400">Offset Y (px)</span>
          <input
            type="number"
            min={-CLAMP}
            max={CLAMP}
            step={1}
            value={offsetY}
            onChange={(e) => onChange({ offset_y: Math.max(-CLAMP, Math.min(CLAMP, Number(e.target.value) || 0)) })}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"
            data-testid="input-offset-y"
          />
        </label>
        <div className="col-span-2 text-[10px] text-slate-500 leading-tight">
          Base: centro do ecrã. ↑↓←→ movem {STEP}px (<kbd className="px-1 bg-slate-200 rounded text-[9px]">Shift</kbd>+clique = {BIG_STEP}px).
        </div>
      </div>
    </div>
  );
}

function PhraseRow({ phrase, onChange, onSave, saving, onFocus, isSelected, previewLang }) {
  const val = (k) => phrase[k] ?? "";
  const offsetX = Number(val("offset_x")) || 0;
  const offsetY = Number(val("offset_y")) || 0;

  return (
    <div
      className={`rounded-2xl border shadow-sm p-4 flex flex-col gap-3 transition ${
        isSelected ? "bg-sky-50 border-sky-400 ring-2 ring-sky-200" : "bg-white border-slate-200"
      }`}
      data-testid={`demo-phrase-card-${phrase.key}`}
      onClick={onFocus}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-400">{phrase.key}</div>
          <div className="text-sm font-bold text-slate-900">
            {PHRASE_LABELS[phrase.key] || phrase.key}
          </div>
        </div>
        <button
          data-testid={`toggle-enabled-${phrase.key}`}
          onClick={(e) => { e.stopPropagation(); onChange({ enabled: !phrase.enabled }); }}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
            phrase.enabled
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {phrase.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
          {phrase.enabled ? "Ativa" : "Oculta"}
        </button>
      </div>

      {/* Language text fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {LANG_OPTIONS.map((lo) => (
          <label key={lo.v} className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase text-slate-400">Texto {lo.l}</span>
            <input
              data-testid={`input-text-${phrase.key}-${lo.v}`}
              value={val(`text_${lo.v}`)}
              onChange={(e) => onChange({ [`text_${lo.v}`]: e.target.value })}
              onFocus={onFocus}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
            />
          </label>
        ))}
      </div>

      {/* Position pad */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
        <div className="text-[10px] font-bold uppercase text-slate-500 mb-2">Posição no ecrã</div>
        <ArrowPad offsetX={offsetX} offsetY={offsetY} onChange={onChange} />
      </div>

      {/* Visual properties */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-400">Cor</span>
          <div className="flex items-center gap-1.5">
            <input
              type="color"
              data-testid={`input-color-${phrase.key}`}
              value={val("color") || "#FDE047"}
              onChange={(e) => onChange({ color: e.target.value })}
              className="w-9 h-9 border border-slate-200 rounded"
            />
            <input
              value={val("color") || ""}
              onChange={(e) => onChange({ color: e.target.value })}
              className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs w-full font-mono"
            />
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-400">Tam. fonte (px)</span>
          <input
            type="number"
            min={10}
            max={120}
            data-testid={`input-font-${phrase.key}`}
            value={val("font_size") || 28}
            onChange={(e) => onChange({ font_size: Math.max(10, Math.min(120, Number(e.target.value) || 28)) })}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-400">Efeito</span>
          <select
            data-testid={`select-effect-${phrase.key}`}
            value={val("effect") || "fade"}
            onChange={(e) => onChange({ effect: e.target.value })}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white"
          >
            {EFFECT_OPTIONS.map((o) => (
              <option key={o.v} value={o.v}>{o.l}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase text-slate-400">Duração (ms)</span>
          <input
            type="number"
            min={300}
            max={15000}
            step={100}
            data-testid={`input-duration-${phrase.key}`}
            value={val("duration_ms") || 2200}
            onChange={(e) => onChange({ duration_ms: Math.max(300, Math.min(15000, Number(e.target.value) || 2200)) })}
            className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] text-slate-500">
          <b>{previewLang.toUpperCase()}</b>: “
          <span
            style={{
              color: phrase.color || "#FDE047",
              fontWeight: 800,
              padding: "0 4px",
              background: "#111827",
              borderRadius: 4,
            }}
          >
            {phrase[`text_${previewLang}`] || phrase.text_pt || "…"}
          </span>”
        </div>
        <button
          data-testid={`save-${phrase.key}`}
          onClick={(e) => { e.stopPropagation(); onSave(phrase.key); }}
          disabled={saving}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-bold bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </button>
      </div>
    </div>
  );
}

/**
 * MockGameFrame — lightweight fake gameplay board. The preview DemoOverlay
 * is rendered OVER this frame, centred and offset by the configured
 * (offset_x, offset_y) — matching the real-game behaviour exactly.
 */
function MockGameFrame() {
  return (
    <div className="relative w-full aspect-video rounded-2xl bg-gradient-to-br from-sky-900 via-blue-800 to-indigo-900 overflow-hidden border-4 border-slate-800 shadow-2xl">
      <div className="absolute inset-0 grid grid-cols-12 gap-3 p-4">
        <div className="col-span-3 flex items-center justify-center">
          <div className="w-full aspect-square rounded-xl bg-gradient-to-br from-blue-900 to-blue-700 border-4 border-slate-900 flex items-center justify-center">
            <div className="text-amber-300 font-[Fredoka] font-black text-4xl">S</div>
          </div>
        </div>
        <div className="col-span-6 flex items-center justify-center">
          <div className="w-full aspect-[1.4/1] rounded-xl bg-white border-4 border-slate-900 grid grid-cols-2 gap-1 p-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="bg-slate-100 rounded text-[10px] text-slate-500 flex items-center justify-center font-mono">
                {i * 3 + 2}
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-3 flex flex-col gap-2">
          <div className="rounded-lg bg-lime-400/90 h-8 flex items-center justify-center text-slate-900 font-bold text-sm">
            00 : 08
          </div>
          <div className="rounded-lg bg-white h-14 flex items-center justify-center text-slate-400 text-xs font-mono">
            SUPERT bar
          </div>
          <div className="rounded-lg bg-white h-10 flex items-center gap-1 px-2">
            {["S","U","P","E","R","T"].map((L) => (
              <div key={L} className="w-4 h-6 rounded bg-blue-700 text-amber-300 text-[8px] flex items-center justify-center font-black">
                {L}
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Center crosshair so admins can visualize (0,0) */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white" />
        <div className="absolute left-0 right-0 top-1/2 h-px bg-white" />
      </div>
    </div>
  );
}

export default function AdminDemoPhrases() {
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [dirty, setDirty] = useState({});
  const [selectedKey, setSelectedKey] = useState("phrase_1");
  const [previewLang, setPreviewLang] = useState("pt");
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/demo/phrases");
      setPhrases(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a carregar frases");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const applyChange = (key, patch) => {
    setPhrases((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));
    setDirty((d) => ({ ...d, [key]: { ...(d[key] || {}), ...patch } }));
  };

  const savePhrase = async (key) => {
    if (!dirty[key]) {
      toast.info("Sem alterações para guardar");
      return;
    }
    setSavingKey(key);
    try {
      const { data } = await api.put(`/admin/demo/phrases/${key}`, dirty[key]);
      setPhrases((prev) => prev.map((p) => (p.key === key ? data : p)));
      setDirty((d) => { const c = { ...d }; delete c[key]; return c; });
      toast.success("Frase guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a guardar");
    } finally {
      setSavingKey(null);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Repor todas as frases para os valores por defeito?")) return;
    try {
      await api.post("/admin/demo/phrases/reset");
      setDirty({});
      await load();
      toast.success("Frases repostas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro a repor");
    }
  };

  const selectedPhrase = phrases.find((p) => p.key === selectedKey);
  useEffect(() => { seqRef.current += 1; }, [selectedPhrase?.text_pt, selectedPhrase?.text_en, selectedPhrase?.text_es, selectedPhrase?.color, selectedPhrase?.font_size, selectedPhrase?.offset_x, selectedPhrase?.offset_y, selectedPhrase?.effect]);
  // Scale the real pixel offsets to the preview frame's size so admins get
  // a faithful idea of where the phrase will land on real screens. We
  // target a reference viewport of 1600px wide; the mock frame measures
  // around that in a wide admin layout — the ratio reads at render time.
  const previewRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);
  useEffect(() => {
    if (!previewRef.current) return;
    const update = () => {
      const w = previewRef.current?.clientWidth || 600;
      setPreviewScale(w / 1600);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(previewRef.current);
    return () => ro.disconnect();
  }, []);
  const livePhrase = selectedPhrase
    ? {
        __id: `live-${selectedKey}-${seqRef.current}`,
        text: selectedPhrase[`text_${previewLang}`] || selectedPhrase.text_pt || "",
        color: selectedPhrase.color,
        font_size: Math.max(10, Math.round((selectedPhrase.font_size || 28) * previewScale)),
        offset_x: Math.round((Number(selectedPhrase.offset_x) || 0) * previewScale),
        offset_y: Math.round((Number(selectedPhrase.offset_y) || 0) * previewScale),
        effect: selectedPhrase.effect || "fade",
      }
    : null;

  return (
    <div className="max-w-7xl mx-auto" data-testid="admin-demo-phrases">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">DEMO — Aprender a Jogar</h1>
          <p className="text-sm text-slate-500 mt-1">
            Frases-guia mostradas durante a demonstração automática. Cada frase arranca no centro do ecrã; usa as setas para a mover para a posição exata onde queres que apareça.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            Prévia em
            <select
              data-testid="preview-lang-select"
              value={previewLang}
              onChange={(e) => setPreviewLang(e.target.value)}
              className="border border-slate-200 rounded-lg px-2 py-1 bg-white"
            >
              {LANG_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </label>
          <button
            data-testid="reset-all-btn"
            onClick={resetAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            <RefreshCcw className="w-4 h-4" /> Repor
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 flex flex-col gap-4">
            {phrases.map((p) => (
              <PhraseRow
                key={p.key}
                phrase={p}
                onChange={(patch) => applyChange(p.key, patch)}
                onSave={savePhrase}
                saving={savingKey === p.key}
                onFocus={() => setSelectedKey(p.key)}
                isSelected={selectedKey === p.key}
                previewLang={previewLang}
              />
            ))}
          </div>

          <div className="lg:col-span-2">
            <div className="sticky top-4 flex flex-col gap-3">
              <div className="text-xs font-bold uppercase text-slate-500 tracking-wide flex items-center justify-between">
                <span>Pré-visualização ao vivo</span>
                {selectedPhrase && (
                  <span className="text-[10px] bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">
                    {PHRASE_LABELS[selectedPhrase.key] || selectedPhrase.key}
                  </span>
                )}
              </div>
              <div className="relative" data-testid="live-preview-frame" ref={previewRef}>
                <MockGameFrame />
                {/* Scoped overlay — only inside the preview frame, not the
                    whole admin screen. Framer-motion still fades + slides. */}
                {livePhrase && (
                  <div className="absolute inset-0 overflow-hidden rounded-2xl pointer-events-none">
                    <DemoOverlay
                      phrase={livePhrase}
                      containerClassName="absolute inset-0 pointer-events-none"
                    />
                  </div>
                )}
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                <b>Como usar:</b> clica num cartão de frase para o selecionar. Usa as 4 setas para mover a frase pixel a pixel; o botão central <b>⊙</b> recentra. Mantém <kbd className="px-1 bg-slate-200 rounded">Shift</kbd> para saltos de 50px. A pré-visualização reflete a escala proporcional ao ecrã do jogador.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
